import React from 'react';
import { getSpotGalleryUrls } from './photoModel';
import { getRejectionCauseLabel, isUnsplashQueue } from './model';

// The headline of a proposal names the action about to be taken, never what the
// model made of the photo. "Located, but not convincingly" was the model's
// opinion, and it left the one thing the owner is reading for - what happens if
// he approves this - to be inferred from it. The opinion belongs in the reason
// underneath.
const ACTION_COPY = {
  assign_spot: {
    eyebrow: 'Assign to existing spot',
    tone: 'assign',
  },
  create_spot: {
    eyebrow: 'Create a new spot',
    tone: 'create',
  },
  reject: {
    eyebrow: 'Reject this photo',
    tone: 'reject',
  },
  needs_more_info: {
    eyebrow: 'Ask the app’s users to locate it',
    tone: 'reject',
  },
  needs_review: {
    eyebrow: 'Leave pending for you to decide in Mod',
    tone: 'review',
  },
  needs_gps_fix: {
    eyebrow: 'Leave pending for you to place the point',
    tone: 'review',
  },
};

// The same abstention does two different things depending on where the photo
// came from. On a user photo it really is left for the owner; on a photo from
// the unsplash collection it is parked and handed back to the app's users,
// exactly like needs_more_info, so it has to say that instead.
const PARKED_EYEBROW = 'Ask the app’s users to locate it';

const getActionEyebrow = (action, photo) => {
  const copy = ACTION_COPY[action] || ACTION_COPY.needs_review;
  const queue = (photo && photo.queue) || 'user_photos';
  const isFromUnsplash = isUnsplashQueue(queue);

  return isFromUnsplash && (action === 'needs_review' || action === 'needs_gps_fix')
    ? PARKED_EYEBROW
    : copy.eyebrow;
};

// The whole workspace paints itself with the action's colour - the photo stage
// as much as the inspector - so the tone is read one level up.
export const getSuggestionTone = suggestion => {
  const copy = suggestion && ACTION_COPY[suggestion.action];

  return copy ? copy.tone : 'idle';
};

const CREATE_ALERT_RADIUS = 150;

// Creating a spot with nothing around it is a safe, full green. The closer an
// existing spot sits, the further the whole workspace drifts towards amber, so
// a possible duplicate is felt before the candidate list is read.
export const getSuggestionToneStyle = suggestion => {
  if (!suggestion || suggestion.action !== 'create_spot') {
    return undefined;
  }

  const distances = (Array.isArray(suggestion.candidates) ? suggestion.candidates : [])
    .map(candidate => Number(candidate.distanceMeters))
    .filter(distance => Number.isFinite(distance));

  if (distances.length === 0) {
    return undefined;
  }

  const ratio = Math.min(Math.min(...distances) / CREATE_ALERT_RADIUS, 1);
  const hue = Math.round(38 + ratio * 38);

  return {
    '--auto-tone': `hsl(${hue}, 100%, 71%)`,
    '--auto-tone-wash': `hsla(${hue}, 100%, 71%, 0.2)`,
    '--auto-tone-stage': `hsla(${hue}, 100%, 71%, 0.34)`,
    '--auto-tone-glow': `hsla(${hue}, 100%, 71%, 0.28)`,
  };
};

const formatValue = value => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return 'Not provided';
  }

  if (typeof value === 'boolean') {
    return value ? 'Passed' : 'Needs attention';
  }

  if (Array.isArray(value)) {
    const primitiveValues = value.filter(item => ['string', 'number'].includes(typeof item));
    return primitiveValues.length === value.length
      ? primitiveValues.join(' · ')
      : `${value.length} item${value.length === 1 ? '' : 's'}`;
  }

  if (typeof value === 'object') {
    return (
      value.detail ||
      value.reason ||
      value.value ||
      value.label ||
      value.message ||
      value.status ||
      'Available'
    );
  }

  return String(value);
};

const formatDistance = value =>
  Number.isFinite(Number(value)) ? `${Math.round(Number(value))} m away` : 'Distance unavailable';

// 20 m or less is the same photographic spot, and by 250 m it is somewhere else.
// Green there, red here - candidates further out are sent only when the area is
// empty, and they read as red because they need the image to justify them.
const getDistanceColor = value => {
  const meters = Number(value);

  if (!Number.isFinite(meters)) {
    return undefined;
  }

  const ratio = Math.min(Math.max((meters - 20) / 230, 0), 1);

  return `hsl(${Math.round(126 - ratio * 126)}, 82%, 60%)`;
};

const AssignmentProposal = ({ suggestion, hydratedTargetSpot, isHydrating, hydrationFailed }) => {
  const spot = { ...(suggestion.targetSpot || {}), ...(hydratedTargetSpot || {}) };
  const galleryUrls = getSpotGalleryUrls(spot).slice(0, 4);

  return (
    <div className="auto-proposal-body">
      <div className="auto-target-heading">
        <div>
          <span>Existing NoFilter spot</span>
          <h2>{spot.name || spot.slug || 'Unnamed spot'}</h2>
          <p>{[spot.from, spot.country].filter(Boolean).join(' · ') || 'Location details unavailable'}</p>
        </div>
        <strong style={{ color: getDistanceColor(spot.distanceMeters) }}>
          {formatDistance(spot.distanceMeters)}
        </strong>
      </div>

      {galleryUrls.length > 0 && (
        <div className="auto-target-gallery" aria-label={`Photos from ${spot.name || 'target spot'}`}>
          {galleryUrls.map((url, index) => (
            <img src={url} alt="" key={`${spot.id || 'spot'}-${index}`} />
          ))}
        </div>
      )}

      {isHydrating && <p className="auto-inline-status">Loading the spot gallery…</p>}
      {hydrationFailed && (
        <p className="auto-inline-status --warning">
          The gallery could not load. The target id is still available for review.
        </p>
      )}

      <dl className="auto-exact-fields">
        <div><dt>Name</dt><dd>{spot.name || <span className="--empty">Not provided</span>}</dd></div>
        <div><dt>From</dt><dd>{spot.from || <span className="--empty">Not provided</span>}</dd></div>
        <div><dt>Country</dt><dd>{spot.country || <span className="--empty">Not provided</span>}</dd></div>
        <div><dt>Slug</dt><dd>{spot.slug || <span className="--empty">Not provided</span>}</dd></div>
        <div><dt>Spot ID</dt><dd className="--quiet">{spot.id}</dd></div>
      </dl>
    </div>
  );
};

const CreationProposal = ({ suggestion }) => {
  const spot = suggestion.newSpot || {};
  // Not Number(): null coerces to 0, which is finite, so every untouched point
  // announced itself as having been moved zero metres.
  const movedMeters =
    typeof spot.movedFromPhotoMeters === 'number' && spot.movedFromPhotoMeters > 0
      ? spot.movedFromPhotoMeters
      : null;
  const fields = [
    ['Name', spot.name],
    ['From', spot.from],
    ['City (slug only)', spot.city],
    ['Country', spot.country],
    ['Slug', spot.slug],
  ];

  return (
    <div className="auto-proposal-body">
      {/* The spot will not be created where the photo says it was taken. That
          cannot be a detail buried in the reason text. */}
      {movedMeters !== null && (
        <div className="auto-moved-point">
          <span className="auto-kicker">The point was moved</span>
          <strong>{Math.round(movedMeters)} m onto {spot.movedToPoiName || 'a nearby place'}</strong>
          <p>The pin looked wrong, so the spot lands on that place instead of on the photo’s own coordinates.</p>
        </div>
      )}

      <dl className="auto-create-fields">
        {fields.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value || <span className="--empty">Not provided</span>}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const RejectionProposal = ({ suggestion }) => {
  const rejection = suggestion.rejection || {};
  const label = rejection.label || getRejectionCauseLabel(rejection.cause);

  return (
    <div className="auto-proposal-body auto-rejection-proposal">
      <span>Canonical rejection cause</span>
      <div>
        <strong>{rejection.cause}</strong>
        <h2>{label}</h2>
      </div>
      {rejection.reason && <p>{rejection.reason}</p>}
    </div>
  );
};

// The same action means two different things depending on which queue the photo
// came from, and the Detective wording was being shown for both.
//
// A Detective photo only got here because somebody already placed it on a map,
// so it is never "nobody answered": what the model is saying is that their
// answers do not stand up - they contradict each other, or the one point on
// offer is not somewhere this photo could have been taken from. An Unsplash
// photo has no answers at all. Nobody has ever been asked about it, and what the
// model is saying is that the coordinate the photo arrived with cannot be
// believed. Pointing that one at "the answers below" points at nothing.
const MoreInfoProposal = ({ suggestion, photo }) => {
  const isDetective = Boolean(photo && photo.queue === 'detective');

  return (
    <div className="auto-proposal-body auto-rejection-proposal">
      <span>Back to the app</span>
      <div>
        <h2>{isDetective ? 'The answers below don’t place it' : 'Nobody has placed it yet'}</h2>
      </div>
      {suggestion.reason && <p>{suggestion.reason}</p>}
      <p>
        {isDetective
          ? 'The photo stays published and keeps being shown to users until somebody places it somewhere that fits. It only leaves the Detective list here.'
          : 'The photo stays published and keeps being offered to the app’s users, who are asked where the photographer was standing. It only leaves your moderation list here.'}
      </p>
    </div>
  );
};

// Who located this photo, and where they said the photographer was standing. The
// point that is about to become a spot came from one of these people, so the
// owner has to be able to see all of them and how much they agreed.
const DetectiveAnswers = ({ photo, chosenStandpointId }) => {
  const standpoints = (photo && Array.isArray(photo.standpoints) && photo.standpoints) || [];

  if (standpoints.length === 0) {
    return null;
  }

  return (
    <section className="auto-inspector-section auto-nearby">
      <div className="auto-section-heading">
        <span className="auto-kicker">Who found it</span>
        <h2>
          {standpoints.length} standpoint{standpoints.length === 1 ? '' : 's'} from{' '}
          {photo.answerCount || standpoints.length} answer
          {(photo.answerCount || standpoints.length) === 1 ? '' : 's'}
        </h2>
      </div>

      <ul className="auto-nearby-list">
        {standpoints.map((standpoint, index) => (
          <li
            className={standpoint.id === chosenStandpointId ? '--chosen' : ''}
            key={standpoint.id}
          >
            <div>
              <strong>
                {standpoint.names && standpoint.names.length > 0
                  ? standpoint.names.join(' · ')
                  : 'No name typed'}
              </strong>
              <span>
                {standpoint.froms && standpoint.froms.length > 0
                  ? `from ${standpoint.froms.join(' · ')}`
                  : 'no “from” typed'}
              </span>
              <small>
                {standpoint.agreement > 1
                  ? `${standpoint.agreement} people agreed`
                  : 'one answer'}
                {index === 0 ? ' · used for the candidates' : ''}
              </small>
            </div>
            {standpoint.id === chosenStandpointId && (
              <span className="auto-nearby-badge">Chosen</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const NearbyCandidates = ({ candidates, targetSpotId }) => (
  <section className="auto-inspector-section auto-nearby">
    <div className="auto-section-heading">
      <span className="auto-kicker">What the model compared</span>
      <h2>
        {candidates.length} nearby spot{candidates.length === 1 ? '' : 's'}
      </h2>
    </div>

    <ul className="auto-nearby-list">
      {candidates.map(candidate => (
        <li
          className={candidate.id === targetSpotId ? '--chosen' : ''}
          key={candidate.id}
        >
          {candidate.mainImageUrl ? (
            <img src={candidate.mainImageUrl} alt="" />
          ) : (
            <div className="auto-nearby-thumb-empty" aria-hidden="true" />
          )}
          <div>
            <strong>{candidate.name || candidate.slug || candidate.id}</strong>
            <span>
              {[candidate.from, candidate.country].filter(Boolean).join(' · ') || candidate.slug}
            </span>
            <small>
              {formatDistance(candidate.distanceMeters)} ·{' '}
              {Number(candidate.userPhotoCount || 0) + Number(candidate.photoCount || 0)} photos
            </small>
          </div>
          {candidate.id === targetSpotId && <span className="auto-nearby-badge">Proposed</span>}
        </li>
      ))}
    </ul>
  </section>
);

const SuggestionPanel = ({
  suggestion,
  photo,
  hydratedTargetSpot,
  isHydratingTarget,
  targetHydrationFailed,
}) => {
  const actionCopy = ACTION_COPY[suggestion.action] || ACTION_COPY.needs_review;
  const actionEyebrow = getActionEyebrow(suggestion.action, photo);
  const candidates = Array.isArray(suggestion.candidates) ? suggestion.candidates : [];
  const isDetective = Boolean(photo && photo.queue === 'detective');
  const isFromUnsplash = isUnsplashQueue(photo && photo.queue);
  const chosenStandpointId =
    (suggestion.newSpot && suggestion.newSpot.standpointDetectiveId) || '';
  const previousRejection = suggestion.previousRejection || null;
  // A proposal only had the owner's words in front of it when the backend
  // actually sent them. A suggestion generated before an earlier correction, or
  // before corrections were sent at all, has to say so: otherwise a proposal
  // that never saw the order looks exactly like one that ignored it.
  const sawCorrections = Number(suggestion.ownerCorrectionsCount) > 0;
  const targetSpotId = (suggestion.targetSpot && suggestion.targetSpot.id) || '';
  const confidence = Number(suggestion.confidence);
  const confidenceLabel = Number.isFinite(confidence)
    ? `${Math.round(Math.max(0, Math.min(1, confidence)) * 100)}%`
    : 'n/a';

  // Nothing was proposed: say so and get out of the way. On a photo from the
  // unsplash collection this is not "pending for Mod" at all - approving it
  // parks the photo and hands it back to the app, exactly like needs_more_info -
  // so this early return has to name the action too, instead of skipping past
  // the eyebrow that already does.
  if (suggestion.action === 'needs_review') {
    if (isFromUnsplash) {
      return (
        <div className="auto-suggestion --review">
          <div className="auto-unknown">
            <span className="auto-kicker">Back to the app</span>
            <h1>{actionEyebrow}</h1>
            {suggestion.reason && <p>{suggestion.reason}</p>}
            <p>
              The photo stays published and keeps being offered to the app’s users, who are asked
              where the photographer was standing. It only leaves your moderation list here.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="auto-suggestion --review">
        <div className="auto-unknown">
          <span className="auto-kicker">No proposal</span>
          <h1>I don’t know here</h1>
          <p>This photo stays pending for Mod.</p>
        </div>
      </div>
    );
  }

  // Not an abstention: the model knows what this is and wants it published. The
  // only thing it could not work out is where the photographer was standing.
  if (suggestion.action === 'needs_gps_fix') {
    return (
      <div className="auto-suggestion --review">
        <div className="auto-unknown">
          <span className="auto-kicker">Worth keeping</span>
          <h1>Place this point by hand</h1>
          <p>{suggestion.reason || 'The submitted point is wrong, but the photo is worth saving.'}</p>
          <p>Find the standpoint on Street View in Mod. The photo stays pending until you do.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`auto-suggestion --${actionCopy.tone}`}>
      <div className="auto-suggestion-header">
        <div>
          <h1>{actionEyebrow}</h1>
        </div>
        <div className="auto-confidence" title="Model confidence">
          <strong>{confidenceLabel}</strong>
          <span>confidence</span>
        </div>
      </div>

      {previousRejection && (
        <div className="auto-previous-rejection">
          <span className="auto-kicker">You already corrected this photo</span>
          <p>
            <strong>{previousRejection.feedback || 'No feedback was recorded.'}</strong>
          </p>
          <small>
            The previous proposal was{' '}
            {ACTION_COPY[previousRejection.action]
              ? getActionEyebrow(previousRejection.action, photo).toLowerCase()
              : previousRejection.action}
            .{' '}
            {sawCorrections
              ? 'The proposal above was written with your words in front of it, and they outrank everything the model would otherwise have concluded.'
              : previousRejection.feedback
              ? 'The proposal above was written before you said this, so the model never saw it. Reject it again and the next one will.'
              : 'Nothing was written down for the model to read, so the proposal above was made the same way as the first one.'}
          </small>
        </div>
      )}

      {suggestion.action === 'assign_spot' && (
        <AssignmentProposal
          suggestion={suggestion}
          hydratedTargetSpot={hydratedTargetSpot}
          isHydrating={isHydratingTarget}
          hydrationFailed={targetHydrationFailed}
        />
      )}
      {suggestion.action === 'create_spot' && <CreationProposal suggestion={suggestion} />}
      {suggestion.action === 'reject' && <RejectionProposal suggestion={suggestion} />}
      {suggestion.action === 'needs_more_info' && (
        <MoreInfoProposal suggestion={suggestion} photo={photo} />
      )}

      {Array.isArray(suggestion.warnings) && suggestion.warnings.length > 0 && (
        <section className="auto-warnings">
          <span>Warnings</span>
          <ul>
            {suggestion.warnings.map((warning, index) => (
              <li key={`${String(warning)}-${index}`}>{formatValue(warning)}</li>
            ))}
          </ul>
        </section>
      )}

      {isDetective && (
        <DetectiveAnswers photo={photo} chosenStandpointId={chosenStandpointId} />
      )}

      {candidates.length > 0 && (
        <NearbyCandidates candidates={candidates} targetSpotId={targetSpotId} />
      )}

    </div>
  );
};

export default SuggestionPanel;
